import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, Calendar, DollarSign, Package, LogOut } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      navigate('/login');
      return;
    }
    
    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    fetchBookings(parsedUser.email);
  }, [navigate]);

  const fetchBookings = async (email) => {
    try {
      const response = await axios.get(`${API}/bookings/user/${email}`);
      setBookings(response.data);
    } catch (error) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    toast.success('Logged out successfully');
    navigate('/');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'completed':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#f4f4f0] py-12">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="mb-4 text-[#1a4d2e] hover:text-[#ff6b00]"
              data-testid="back-to-home-button"
            >
              <ChevronLeft className="w-5 h-5 mr-2" />
              Back to Home
            </Button>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#1a4d2e]">
              Welcome, {user.name}
            </h1>
            <p className="text-[#8d6e63] text-lg mt-2">{user.farm_name}</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-2 border-[#1a4d2e] text-[#1a4d2e] hover:bg-[#1a4d2e] hover:text-white"
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Logout
          </Button>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="border border-[#e5ddd5]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#8d6e63] mb-1">Total Bookings</p>
                  <p className="font-heading text-3xl font-bold text-[#1a4d2e]">
                    {bookings.length}
                  </p>
                </div>
                <Calendar className="w-12 h-12 text-[#ff6b00]" strokeWidth={1.5} />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-[#e5ddd5]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#8d6e63] mb-1">Confirmed</p>
                  <p className="font-heading text-3xl font-bold text-[#1a4d2e]">
                    {bookings.filter(b => b.status === 'confirmed').length}
                  </p>
                </div>
                <Package className="w-12 h-12 text-green-600" strokeWidth={1.5} />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-[#e5ddd5]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#8d6e63] mb-1">Total Spent</p>
                  <p className="font-heading text-3xl font-bold text-[#1a4d2e]">
                    ${bookings.reduce((sum, b) => sum + (b.total_cost_incl_gst || b.total_cost || 0), 0).toFixed(2)}
                  </p>
                </div>
                <DollarSign className="w-12 h-12 text-[#ff6b00]" strokeWidth={1.5} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bookings List */}
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-heading text-2xl font-bold text-[#1a4d2e]">
              Your Bookings
            </h2>
            <Button
              onClick={() => navigate('/book')}
              className="bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white"
              data-testid="new-booking-button"
            >
              New Booking
            </Button>
          </div>

          {loading ? (
            <p className="text-center text-[#8d6e63] py-12">Loading bookings...</p>
          ) : bookings.length === 0 ? (
            <Card className="border border-[#e5ddd5]">
              <CardContent className="p-12 text-center">
                <p className="text-[#8d6e63] mb-4">No bookings yet</p>
                <Button
                  onClick={() => navigate('/book')}
                  className="bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white"
                >
                  Create Your First Booking
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4" data-testid="bookings-list">
              {bookings.map((booking, idx) => (
                <motion.div
                  key={booking.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1, duration: 0.4 }}
                >
                  <Card className="border border-[#e5ddd5] hover:border-[#1a4d2e] transition-all">
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="font-heading text-xl text-[#1a4d2e] mb-2">
                            Booking #{booking.id.substring(0, 8)}
                          </CardTitle>
                          <p className="text-sm text-[#8d6e63]">
                            Preferred Date: {booking.preferred_date}
                          </p>
                        </div>
                        <Badge className={getStatusColor(booking.status)}>
                          {booking.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm font-semibold text-[#1a4d2e] mb-2">Services:</p>
                          <div className="space-y-2">
                            {booking.services.map((service, idx) => (
                              <div key={idx} className="flex items-center justify-between bg-[#f4f4f0] p-2 rounded">
                                <span className="text-sm text-[#1a4d2e] font-medium">
                                  {service.service_name || service.service_id}
                                </span>
                                <span className="text-xs text-[#8d6e63]">
                                  {service.num_animals} animals
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <Separator />
                        <div className="grid md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-[#8d6e63]">Preferred Date:</span>
                            <span className="ml-2 font-semibold text-[#1a4d2e]">
                              {booking.preferred_date}
                              {booking.preferred_time && ` at ${booking.preferred_time}`}
                            </span>
                          </div>
                          <div>
                            <span className="text-[#8d6e63]">Total (incl. GST):</span>
                            <span className="ml-2 font-mono font-bold text-[#ff6b00]">
                              ${(booking.total_cost_incl_gst || booking.total_cost || 0).toFixed(2)}
                            </span>
                          </div>
                        </div>
                        {booking.notes && (
                          <div className="mt-3 p-3 bg-[#f4f4f0] rounded-md">
                            <p className="text-sm text-[#4a4a4a]">
                              <strong>Notes:</strong> {booking.notes}
                            </p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
